import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Search, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useYouTube } from "@/context/YouTubeContext";
import { scrollToId } from "@/components/SmoothScroll";

export const SearchOverlay = ({ open, onClose }) => {
  const { t } = useLang();
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const { videos } = useYouTube();
  const index = useMemo(
    () => [
      ...videos.map((v) => ({ title: v.title, sub: t.interviews.liveTag, target: "interviews" })),
      ...t.interviews.items.map((i) => ({ title: i.title, sub: i.guest, target: "interviews" })),
      ...t.ground.items.map((i) => ({ title: i.title, sub: i.category, target: "ground-reports" })),
      ...t.politics.items.map((i) => ({
        title: i.title,
        sub: t.politics.filterLabels[i.category] || i.category,
        target: "politics",
      })),
    ],
    [t, videos]
  );

  const results = query.trim()
    ? index.filter((i) => `${i.title} ${i.sub}`.toLowerCase().includes(query.trim().toLowerCase()))
    : index.slice(0, 5);

  const pick = (target) => {
    onClose();
    setTimeout(() => scrollToId(target), 60);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="search-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[70] bg-ink/90 backdrop-blur-xl"
        >
          <div className="mx-auto max-w-2xl px-5 pt-24">
            <div className="flex items-center gap-3 border-b border-line pb-4">
              <Search className="h-5 w-5 text-ember" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.nav.searchPlaceholder}
                data-testid="search-input"
                className="w-full bg-transparent font-display text-xl text-cream placeholder:text-ash/60 focus:outline-none md:text-2xl"
              />
              <button
                type="button"
                onClick={onClose}
                data-testid="search-close-button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-line text-ash hover:text-cream"
                aria-label="Close search"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-6 max-h-[55vh] overflow-y-auto">
              {results.length === 0 && (
                <p className="py-8 text-center text-sm text-ash" data-testid="search-empty">{t.nav.searchEmpty}</p>
              )}
              {results.map((r, i) => (
                <button
                  key={`${r.title}-${i}`}
                  type="button"
                  onClick={() => pick(r.target)}
                  data-testid={`search-result-${i}`}
                  className="group flex w-full items-center justify-between gap-4 border-b border-line/50 py-4 text-left"
                >
                  <span>
                    <span className="block font-display text-lg font-bold text-cream transition-colors duration-200 group-hover:text-ember">
                      {r.title}
                    </span>
                    <span className="mt-1 block text-xs uppercase tracking-widest text-ash">{r.sub}</span>
                  </span>
                  <ArrowUpRight className="h-4 w-4 shrink-0 text-ash transition-colors duration-200 group-hover:text-ember" />
                </button>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
